import { type SECRReportConfig } from '@shared/api/hooks/ccf/secr';
import { PriorYearSourceEnum as PriorYearSource } from '@shared/api/types';
import { type SecrFormValues } from './schema';

export function mapConfigToFormValues(
  config: SECRReportConfig
): Partial<SecrFormValues> {
  const savedSource = config.priorYearSource;
  const previousEmissionsSource =
    (!savedSource || savedSource === PriorYearSource.none) &&
    config.hasPriorYearResult
      ? PriorYearSource.my_emissions
      : (savedSource ?? PriorYearSource.none);

  const lockedPriorMetric =
    previousEmissionsSource === PriorYearSource.my_emissions
      ? (config.priorYearComputed?.intensityMetricType ?? undefined)
      : undefined;

  return {
    previousEmissionsSource,
    intensityMetric:
      lockedPriorMetric ?? config.intensityMetricType ?? undefined,
    currentIntensityValue: config.intensityMetricValue ?? undefined,
    prevIntensityValue: config.priorYearOverrides?.intensityValue ?? undefined,
    stationaryCombustion: {
      prevYear:
        config.priorYearOverrides?.categories.stationaryCombustion?.tonnes ??
        undefined,
      prevYearKwh:
        config.priorYearOverrides?.categories.stationaryCombustion?.kwh ??
        undefined,
    },
    mobileCombustion: {
      prevYear:
        config.priorYearOverrides?.categories.mobileCombustion?.tonnes ??
        undefined,
      prevYearKwh:
        config.priorYearOverrides?.categories.mobileCombustion?.kwh ??
        undefined,
    },
    electricity: {
      prevYear:
        config.priorYearOverrides?.categories.electricity?.tonnes ?? undefined,
      prevYearKwh:
        config.priorYearOverrides?.categories.electricity?.kwh ?? undefined,
    },
    heat: {
      prevYear: config.priorYearOverrides?.categories.heat?.tonnes ?? undefined,
      prevYearKwh: config.priorYearOverrides?.categories.heat?.kwh ?? undefined,
    },
    businessTravel: {
      prevYear:
        config.priorYearOverrides?.categories.businessTravel?.tonnes ??
        undefined,
      prevYearKwh:
        config.priorYearOverrides?.categories.businessTravel?.kwh ?? undefined,
    },
    actionsTaken: config.actionsTaken ?? '',
    methodology: config.methodology ?? '',
  };
}

type SecrConfigWritable = Omit<
  SECRReportConfig,
  | 'currentYear'
  | 'hasPriorYearResult'
  | 'priorYearComputed'
  | 'isConfigComplete'
>;

export function mapFormValuesToPayload(
  data: SecrFormValues
): SecrConfigWritable {
  const payload: SecrConfigWritable = {
    intensityMetricType: data.intensityMetric ?? null,
    intensityMetricValue: data.currentIntensityValue ?? null,
    actionsTaken: data.actionsTaken || null,
    priorYearSource: data.previousEmissionsSource,
    priorYearOverrides: null,
    methodology: data.methodology || null,
  };

  if (data.previousEmissionsSource === 'off_platform') {
    const sc = data.stationaryCombustion;
    const mc = data.mobileCombustion;
    const el = data.electricity;
    const hs = data.heat;
    const bt = data.businessTravel;

    payload.priorYearOverrides = {
      categories: {
        stationaryCombustion: {
          tonnes: sc.prevYear ?? null,
          kwh: sc.prevYearKwh ?? null,
        },
        mobileCombustion: {
          tonnes: mc.prevYear ?? null,
          kwh: mc.prevYearKwh ?? null,
        },
        electricity: {
          tonnes: el.prevYear ?? null,
          kwh: el.prevYearKwh ?? null,
        },
        heat: {
          tonnes: hs.prevYear ?? null,
          kwh: hs.prevYearKwh ?? null,
        },
        businessTravel: {
          tonnes: bt.prevYear ?? null,
          kwh: bt.prevYearKwh ?? null,
        },
      },
      totalTonnes:
        (sc.prevYear ?? 0) +
        (mc.prevYear ?? 0) +
        (el.prevYear ?? 0) +
        (hs.prevYear ?? 0) +
        (bt.prevYear ?? 0),
      totalKwh: null,
      intensityValue: data.prevIntensityValue ?? null,
    };
  }

  return payload;
}
